import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail, CheckCircle2, XCircle, Clock3, AlertTriangle, Download } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_META = {
  sent: { Icon: CheckCircle2, label: "Envoyé", cls: "text-emerald-300 bg-emerald-500/10 border-emerald-500/40" },
  scheduled: { Icon: Clock3, label: "En attente", cls: "text-amber-800 bg-amber-500/10 border-amber-200" },
  cancelled: { Icon: XCircle, label: "Annulé", cls: "text-zinc-400 bg-[var(--surface-alt)] border-[var(--border)]" },
  bounced: { Icon: AlertTriangle, label: "Bounced", cls: "text-rose-300 bg-rose-500/100/10 border-rose-500/40" },
  error: { Icon: AlertTriangle, label: "Erreur", cls: "text-rose-300 bg-rose-500/100/10 border-rose-500/40" },
};

export default function EmailsSent() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/emails/history", { params: filter ? { status: filter, limit: 100 } : { limit: 100 } });
      setItems(data.items || []);
      setStats(data.stats || {});
    } catch { toast.error("Erreur chargement historique"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const exportCSV = () => {
    const header = ["date", "destinataire", "sujet", "statut"];
    const rows = items.map((i) => [i.sent_at || i.send_at || i.created_at, i.to_email, i.subject, i.status]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `prosperr-emails-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <AppShell>
      <div className="px-5 pt-6 pb-2 flex items-center gap-3">
        <button onClick={() => navigate("/emails/settings")} className="w-9 h-9 rounded-full border border-[var(--border)] grid place-items-center hover:bg-black/5">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="font-display text-3xl font-bold text-[var(--text)] tracking-tight" data-testid="emails-sent-title">Emails envoyés</h1>
      </div>

      {/* Stats */}
      <div className="px-5 mt-6 grid grid-cols-3 gap-3 max-w-2xl">
        {[
          { k: "sent_today", label: "Aujourd'hui" },
          { k: "total_sent", label: "Total envoyés" },
          { k: "total_cancelled", label: "Annulés" },
        ].map((s) => (
          <div key={s.k} className="border border-[var(--border)] rounded-2xl p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">{s.label}</div>
            <div className="mt-1 font-display font-bold text-3xl">{stats[s.k] ?? 0}</div>
          </div>
        ))}
      </div>

      {/* Filter + export */}
      <div className="px-5 mt-6 flex items-center gap-2 flex-wrap max-w-2xl">
        {["", "sent", "scheduled", "cancelled", "bounced"].map((f) => (
          <button
            key={f || "all"}
            onClick={() => setFilter(f)}
            className={`rounded-full border px-3.5 h-9 text-xs font-semibold ${
              filter === f ? "bg-emerald-500 text-white border-emerald-500" : "border-[var(--border)] bg-[var(--surface)] hover:bg-black/5"
            }`}
            data-testid={`filter-${f || "all"}`}
          >
            {f === "" ? "Tous" : STATUS_META[f]?.label || f}
          </button>
        ))}
        <div className="flex-1" />
        <Button onClick={exportCSV} variant="outline" className="rounded-full border-[var(--border)] h-9 px-4 text-xs" data-testid="emails-export">
          <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
        </Button>
      </div>

      {/* List */}
      <div className="px-5 mt-6 max-w-2xl pb-24">
        {loading && <div className="text-sm text-zinc-400">Chargement…</div>}
        {!loading && items.length === 0 && (
          <div className="border border-dashed border-[var(--border)] rounded-2xl p-10 text-center text-sm text-zinc-400">
            Aucun email pour l'instant.
          </div>
        )}
        <div className="divide-y divide-[var(--border-soft)]">
          {items.map((item) => {
            const meta = STATUS_META[item.status] || STATUS_META.sent;
            const Icon = meta.Icon;
            return (
              <button
                key={item.queue_id}
                onClick={() => setSelected(item)}
                className="w-full text-left py-4 flex items-start gap-4 hover:bg-black/5 transition-colors px-2 rounded-lg"
                data-testid={`email-row-${item.queue_id}`}
              >
                <div className="w-10 h-10 rounded-full bg-[var(--surface-alt)] grid place-items-center shrink-0">
                  <Mail className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold truncate">{item.subject}</div>
                    <span className={`text-[10px] font-bold uppercase tracking-widest border rounded-full px-2 py-0.5 ${meta.cls}`}>
                      <Icon className="w-3 h-3 inline -mt-0.5 mr-1" /> {meta.label}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-400 mt-1 truncate">
                    À {item.to_name ? `${item.to_name} · ` : ""}{item.to_email}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1">
                    {new Date(item.sent_at || item.send_at || item.created_at).toLocaleString("fr-FR")}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-5" onClick={() => setSelected(null)}>
          <div className="bg-[var(--surface)] rounded-xl max-w-xl w-full max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Email</div>
            <h3 className="mt-1 font-display font-bold text-xl">{selected.subject}</h3>
            <div className="mt-2 text-xs text-zinc-400">À: {selected.to_email}</div>
            <pre className="mt-5 text-sm whitespace-pre-wrap break-words font-sans bg-[var(--surface-alt)] p-4 rounded-lg border border-[var(--border)]">{selected.body_text}</pre>
            <div className="mt-4 flex justify-end">
              <Button onClick={() => setSelected(null)} className="rounded-full bg-black text-white h-10 px-5 text-sm">Fermer</Button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
